import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Switch } from "../ui/switch";
import {
  Plus, Trash2, Save, Send, Loader2, Bell, MessageCircle, Mail, Phone, X,
} from "lucide-react";

const CHANNELS = [
  { key: "whatsapp", label: "WhatsApp", icon: MessageCircle, color: "bg-green-500" },
  { key: "sms",      label: "SMS",      icon: Phone,         color: "bg-blue-500" },
  { key: "email",    label: "Email",    icon: Mail,          color: "bg-purple-500" },
];

const emptyForm = { name: "", phone: "+1", email: "", channels: ["whatsapp", "sms"], is_active: true };

export const DispatchersTab = ({ API, getAuthHeaders }) => {
  const [dispatchers, setDispatchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);   // id being saved
  const [testing, setTesting] = useState(false);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [edits, setEdits] = useState({});       // {id: partial}
  const [diag, setDiag] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/dispatchers`, getAuthHeaders());
      setDispatchers(data.dispatchers || []);
    } catch (e) {
      toast.error("Failed to load dispatchers");
    } finally {
      setLoading(false);
    }
  }, [API, getAuthHeaders]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const toggleChannel = (current, ch) => {
    const set = new Set(current || []);
    if (set.has(ch)) set.delete(ch); else set.add(ch);
    return Array.from(set);
  };

  const localEdit = (id, partial) => setEdits(prev => ({ ...prev, [id]: { ...(prev[id] || {}), ...partial } }));
  const getValue = (d, field) => (edits[d.id] && field in edits[d.id]) ? edits[d.id][field] : d[field];

  const saveOne = async (id) => {
    const payload = edits[id];
    if (!payload) return;
    setSaving(id);
    try {
      await axios.patch(`${API}/admin/dispatchers/${id}`, payload, getAuthHeaders());
      toast.success("Saved");
      setEdits(prev => { const { [id]: _, ...rest } = prev; return rest; });
      await fetchList();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(null);
    }
  };

  const removeDispatcher = async (id, name) => {
    if (!window.confirm(`Remove ${name}? They will stop receiving booking alerts.`)) return;
    try {
      await axios.delete(`${API}/admin/dispatchers/${id}`, getAuthHeaders());
      toast.success("Removed");
      await fetchList();
    } catch {
      toast.error("Delete failed");
    }
  };

  const addDispatcher = async () => {
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Name and phone are required");
      return;
    }
    try {
      await axios.post(`${API}/admin/dispatchers`, form, getAuthHeaders());
      toast.success("Dispatcher added");
      setAdding(false);
      setForm(emptyForm);
      await fetchList();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Add failed");
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await axios.post(`${API}/admin/dispatchers/test`, {}, getAuthHeaders());
      const r = data.result || {};
      toast.success(`Test sent — WA:${r.whatsapp||0} SMS:${r.sms||0} EMAIL:${r.email||0} failed:${r.failed||0}`);
      const errs = Array.isArray(data.errors) ? data.errors : (Array.isArray(r.errors) ? r.errors : []);
      for (const e of errs.slice(0, 5)) {
        toast.warning(e, { duration: 15000 });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test failed");
    } finally {
      setTesting(false);
    }
  };

  const loadDiagnostics = async () => {
    setDiagLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/dispatchers/diagnostics`, getAuthHeaders());
      setDiag(data);
    } catch (e) {
      toast.error("Failed to load diagnostics");
    } finally {
      setDiagLoading(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12">
      <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
    </div>;
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between gap-2 flex-wrap">
            <span className="flex items-center gap-2"><Bell className="w-5 h-5 text-amber-600" /> Dispatch Notifications</span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={sendTest}
                disabled={testing}
                data-testid="dispatch-test-btn"
              >
                {testing ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Send className="w-4 h-4 mr-1.5" />}
                Send Test Alert
              </Button>
              <Button
                size="sm"
                onClick={() => setAdding(a => !a)}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="dispatch-add-btn"
              >
                {adding ? <X className="w-4 h-4 mr-1.5" /> : <Plus className="w-4 h-4 mr-1.5" />}
                {adding ? "Cancel" : "Add Dispatcher"}
              </Button>
            </div>
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            These people receive a "New Booking" alert every time a passenger books. Each one picks their channels — WhatsApp falls back to SMS, then per-dispatcher email.
          </p>
        </CardHeader>

        <CardContent className="space-y-3">
          {adding && (
            <div className="border-2 border-dashed border-amber-300 bg-amber-50 rounded-lg p-4 space-y-3" data-testid="dispatch-add-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Name *</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-dispatcher-name" />
                </div>
                <div>
                  <Label className="text-xs">Phone (E.164) *</Label>
                  <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+13128248601" data-testid="new-dispatcher-phone" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Email (required if Email channel enabled)</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="ops@example.com" data-testid="new-dispatcher-email" />
              </div>
              <div className="flex flex-wrap gap-2">
                {CHANNELS.map(c => {
                  const Icon = c.icon;
                  const active = form.channels.includes(c.key);
                  return (
                    <Badge
                      key={c.key}
                      className={`cursor-pointer ${active ? c.color + " text-white" : "bg-gray-100 text-gray-700"}`}
                      onClick={() => setForm({ ...form, channels: toggleChannel(form.channels, c.key) })}
                      data-testid={`new-dispatcher-ch-${c.key}`}
                    >
                      <Icon className="w-3 h-3 mr-1" />{c.label}
                    </Badge>
                  );
                })}
              </div>
              <Button onClick={addDispatcher} className="bg-green-600 hover:bg-green-700" data-testid="new-dispatcher-save">
                <Save className="w-4 h-4 mr-1.5" /> Save Dispatcher
              </Button>
            </div>
          )}

          {dispatchers.length === 0 && !adding && (
            <div className="text-center py-8 text-muted-foreground" data-testid="dispatch-empty-state">
              <Bell className="w-10 h-10 mx-auto mb-2 text-muted-foreground/50" />
              <p>No dispatchers configured. Add one to start receiving booking alerts.</p>
            </div>
          )}

          {dispatchers.map(d => {
            const hasEdits = !!edits[d.id];
            return (
              <div
                key={d.id}
                className={`border rounded-lg p-3 ${hasEdits ? "border-amber-400 bg-amber-50/50" : "border-gray-200"}`}
                data-testid={`dispatcher-row-${d.id}`}
              >
                <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <Switch
                      checked={getValue(d, "is_active")}
                      onCheckedChange={(checked) => localEdit(d.id, { is_active: checked })}
                      data-testid={`dispatcher-active-${d.id}`}
                    />
                    <Input
                      className="font-semibold max-w-xs"
                      value={getValue(d, "name") || ""}
                      onChange={(e) => localEdit(d.id, { name: e.target.value })}
                      data-testid={`dispatcher-name-${d.id}`}
                    />
                  </div>
                  <div className="flex gap-1">
                    {hasEdits && (
                      <Button
                        size="sm"
                        onClick={() => saveOne(d.id)}
                        disabled={saving === d.id}
                        className="bg-amber-600 hover:bg-amber-700"
                        data-testid={`dispatcher-save-${d.id}`}
                      >
                        {saving === d.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => removeDispatcher(d.id, d.name)}
                      className="text-red-600 hover:bg-red-50"
                      data-testid={`dispatcher-delete-${d.id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
                  <div>
                    <Label className="text-[10px] uppercase text-muted-foreground">Phone</Label>
                    <Input
                      value={getValue(d, "phone") || ""}
                      onChange={(e) => localEdit(d.id, { phone: e.target.value })}
                      placeholder="+13128248601"
                      className="text-sm"
                      data-testid={`dispatcher-phone-${d.id}`}
                    />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase text-muted-foreground">Email</Label>
                    <Input
                      type="email"
                      value={getValue(d, "email") || ""}
                      onChange={(e) => localEdit(d.id, { email: e.target.value })}
                      placeholder="(optional)"
                      className="text-sm"
                      data-testid={`dispatcher-email-${d.id}`}
                    />
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {CHANNELS.map(c => {
                    const Icon = c.icon;
                    const channels = getValue(d, "channels") || [];
                    const active = channels.includes(c.key);
                    return (
                      <Badge
                        key={c.key}
                        className={`cursor-pointer text-xs ${active ? c.color + " text-white" : "bg-gray-100 text-gray-600"}`}
                        onClick={() => localEdit(d.id, { channels: toggleChannel(channels, c.key) })}
                        data-testid={`dispatcher-ch-${d.id}-${c.key}`}
                      >
                        <Icon className="w-3 h-3 mr-1" />{c.label}
                      </Badge>
                    );
                  })}
                </div>
              </div>
            );
          })}

          <div className="text-xs text-muted-foreground border-t pt-3 mt-4">
            <p className="font-semibold mb-1">How fallback works:</p>
            <ol className="list-decimal list-inside space-y-0.5">
              <li>WhatsApp is tried first if enabled for that dispatcher</li>
              <li>If WhatsApp fails (e.g. Twilio sandbox 50/day cap), SMS is tried (requires <code>TWILIO_SMS_FROM</code> env)</li>
              <li>Email is sent independently if enabled and an email address is set</li>
            </ol>
          </div>
        </CardContent>
      </Card>

      {/* Diagnostics Panel */}
      <Card className="border-amber-200" data-testid="dispatch-diagnostics-card">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center justify-between gap-2 text-base">
            <span className="flex items-center gap-2"><MessageCircle className="w-4 h-4 text-amber-600" /> Channel Diagnostics</span>
            <Button variant="outline" size="sm" onClick={loadDiagnostics} disabled={diagLoading} data-testid="load-diagnostics-btn">
              {diagLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              {diag ? "Refresh" : "Run Check"}
            </Button>
          </CardTitle>
        </CardHeader>
        {diag && (
          <CardContent className="space-y-3 pt-0">
            {/* Channel status */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {Object.entries(diag.channels || {}).map(([key, ch]) => (
                <div key={key} className={`rounded-lg p-3 text-xs border ${ch.configured ? "bg-emerald-50 border-emerald-200" : "bg-rose-50 border-rose-200"}`}>
                  <div className="font-bold uppercase mb-1">{key}</div>
                  <div className={ch.configured ? "text-emerald-700" : "text-rose-700 font-semibold"}>
                    {ch.configured ? "✓ Configured" : "✗ Not configured"}
                  </div>
                  {ch.from_number && <div className="text-muted-foreground mt-0.5">From: {String(ch.from_number).slice(0, 40)}</div>}
                  {ch.ops_email && <div className="text-muted-foreground mt-0.5">{ch.ops_email}</div>}
                  {ch.sandbox_mode && <div className="text-amber-600 font-semibold mt-1">⚠ Sandbox Mode</div>}
                </div>
              ))}
            </div>
            {/* Recommendations */}
            {(diag.recommendations || []).length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <div className="text-xs font-bold text-amber-800 mb-1">Action Items:</div>
                <ul className="text-xs text-amber-700 space-y-1 list-disc list-inside">
                  {diag.recommendations.map((r, i) => <li key={i}>{r}</li>)}
                </ul>
              </div>
            )}
          </CardContent>
        )}
      </Card>
    </div>
  );
};
